import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import ScheduleIcon from '@mui/icons-material/Schedule'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useRoadmap } from './RoadmapContext'
import { CS_RESERVE_PCT, ENGAGEMENT_PRESETS, normalizeServiceLine, SERVICE_LINE_LABELS } from './types'

const MONTH_ABBRS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const RoadmapSidebar: React.FC = () => {
  const theme = useTheme()
  const {
    state,
    dispatch,
    phaseSummaries,
    ratePerPoint,
    effectiveRate,
    totalPoints,
    totalInvestment,
    removeItem,
  } = useRoadmap()
  const [expandedPhase, setExpandedPhase] = useState<string | false>(state.activePhase)

  const duration = state.engagementConfig.duration
  const monthlyBudget = state.engagementConfig.monthlyBudget
  const preset = ENGAGEMENT_PRESETS.find(p => p.duration === duration)

  // CS reserve calculation
  const csPointsPerMonth = Math.round(monthlyBudget * CS_RESERVE_PCT)
  const csTotalPoints = csPointsPerMonth * duration
  const csTotalDollars = csTotalPoints * effectiveRate

  // Monthly and setup totals from actual items
  const setupPoints = useMemo(() => {
    return state.lineItems.filter(li => li.phase === 'setup').reduce((sum, li) => sum + li.points * li.quantity, 0)
  }, [state.lineItems])
  const monthlyItemPoints = useMemo(() => {
    return state.lineItems.filter(li => li.phase !== 'setup').reduce((sum, li) => sum + li.points * li.quantity, 0)
  }, [state.lineItems])

  const setupDollars = setupPoints * effectiveRate
  const monthlyDollars = monthlyItemPoints * effectiveRate
  const avgPerMonth = duration > 0 ? Math.round(monthlyItemPoints / duration) : 0
  const avgPerMonthDollars = avgPerMonth * effectiveRate

  // MRR = effective monthly services + CS reserve
  const servicesMonthly = avgPerMonth * effectiveRate
  const csMonthly = csPointsPerMonth * effectiveRate
  const totalMRR = servicesMonthly + csMonthly

  // Total engagement = items + CS reserve
  const totalEngagementPoints = totalPoints + csTotalPoints
  const totalEngagementDollars = totalInvestment + csTotalDollars

  // Subtotals by service line
  const serviceSubtotals = useMemo(() => {
    const groups: Record<string, { points: number; count: number }> = {}
    for (const li of state.lineItems) {
      const sl = normalizeServiceLine(li.service_line)
      if (!groups[sl]) groups[sl] = { points: 0, count: 0 }
      groups[sl].points += li.points * li.quantity
      groups[sl].count += 1
    }
    return Object.entries(groups)
      .map(([code, data]) => ({
        code,
        label: SERVICE_LINE_LABELS[code] || code.toUpperCase(),
        ...data,
      }))
      .sort((a, b) => b.points - a.points)
  }, [state.lineItems])

  // Phase month labels
  const phaseMonthLabels = useMemo(() => {
    const labels: Record<string, string> = {}
    const startMonth = state.engagementConfig.startMonth
    if (!startMonth) return labels
    const [yr, mo] = startMonth.split('-').map(Number)
    for (const s of phaseSummaries) {
      if (s.phase === 'setup') continue
      const monthNum = parseInt(s.phase.replace('month-', ''), 10)
      const totalMonths = yr * 12 + (mo - 1) + (monthNum - 1)
      const calYear = Math.floor(totalMonths / 12)
      const calMonth = totalMonths % 12
      labels[s.phase] = `${MONTH_ABBRS[calMonth]} ${calYear}`
    }
    return labels
  }, [state.engagementConfig.startMonth, phaseSummaries])

  const hasItems = state.lineItems.length > 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Your Package Header — sticky at top ── */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.default' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <SettingsIcon sx={{ fontSize: 16, color: theme.palette.text.complete }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Your Package
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {state.clientName || 'Select a client'}
        </Typography>
        {!hasItems && (
          <Typography variant="caption" sx={{ color: theme.palette.text.complete, mt: 0.5, display: 'block' }}>
            Add services to start building
          </Typography>
        )}
      </Box>

      {/* ── Annual Partnership Summary Card ── */}
      {hasItems && (
        <Box
          sx={{
            mx: 2,
            mb: 1.5,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha(theme.palette.text.complete, 0.2),
            bgcolor: alpha(theme.palette.text.complete, 0.03),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'text.secondary' }}
            >
              {preset?.label || `${duration}-Month Engagement`}
            </Typography>
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', lineHeight: 1.1, mb: 0.25 }}>
            {totalEngagementPoints}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              color: 'text.secondary',
              mb: 1,
            }}
          >
            Total Engagement Points
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              fontSize: 20,
              color: theme.palette.text.complete,
              lineHeight: 1.2,
            }}
          >
            ${totalEngagementDollars.toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', mb: 1.5 }}>
            at ${effectiveRate}/pt
          </Typography>

          {/* Monthly MRR */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScheduleIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Monthly MRR
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              ${totalMRR.toLocaleString()}/mo
            </Typography>
          </Box>
          <Box sx={{ pl: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                Services ({Math.round((1 - CS_RESERVE_PCT) * 100)}%)
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: 10, fontVariantNumeric: 'tabular-nums' }}
              >
                ${servicesMonthly.toLocaleString()}/mo
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                Client Success ({Math.round(CS_RESERVE_PCT * 100)}%)
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: 10, fontVariantNumeric: 'tabular-nums' }}
              >
                ${csMonthly.toLocaleString()}/mo
              </Typography>
            </Box>
          </Box>

          {/* Setup investment */}
          {setupDollars > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AutoAwesomeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Setup investment
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                ${setupDollars.toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ── Phase Timeline ── */}
      <Box sx={{ px: 2, pt: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 1,
            display: 'block',
          }}
        >
          Phase Timeline
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {phaseSummaries.map(summary => {
          const phaseItems = state.lineItems.filter(li => li.phase === summary.phase)
          const fillPct = Math.min(summary.utilization, 100)
          const monthLabel = phaseMonthLabels[summary.phase]

          return (
            <Accordion
              key={summary.phase}
              expanded={expandedPhase === summary.phase}
              onChange={(_, expanded) => {
                setExpandedPhase(expanded ? summary.phase : false)
                if (expanded) dispatch({ type: 'SET_ACTIVE_PHASE', phase: summary.phase })
              }}
              disableGutters
              sx={{
                bgcolor: 'transparent',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '& .MuiAccordionSummary-root': { minHeight: 36 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ px: 2, py: 0 }}>
                <Box sx={{ width: '100%', pr: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
                      {summary.label}
                    </Typography>
                    {monthLabel && (
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                        {monthLabel}
                      </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontSize: 10,
                        color: summary.isOverBudget ? theme.palette.text.error : 'text.secondary',
                      }}
                    >
                      {summary.usedPoints}/{summary.budget} pts
                    </Typography>
                  </Box>
                  {/* Progress bar visible even when collapsed */}
                  <LinearProgress
                    variant="determinate"
                    value={fillPct}
                    sx={{
                      height: 3,
                      borderRadius: 2,
                      bgcolor: summary.isOverBudget
                        ? alpha(theme.palette.text.error, 0.12)
                        : alpha(theme.palette.text.complete, 0.12),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 2,
                        bgcolor: summary.isOverBudget ? theme.palette.text.error : theme.palette.text.complete,
                      },
                    }}
                  />
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 1 }}>
                {phaseItems.length === 0 ? (
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 11 }}>
                    + Click to add items
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {phaseItems.map(li => (
                      <Box
                        key={li.lineItemId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          py: 0.4,
                          px: 0.5,
                          borderRadius: 0.75,
                          '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) },
                          '&:hover .remove-btn': { opacity: 1 },
                        }}
                      >
                        <Chip
                          label={li.category === 'strategic' ? 'S' : 'E'}
                          size="small"
                          sx={{
                            width: 16,
                            height: 16,
                            fontSize: 8,
                            fontWeight: 700,
                            bgcolor:
                              li.category === 'strategic'
                                ? alpha(theme.palette.text.link, 0.15)
                                : alpha(theme.palette.text.highlight, 0.15),
                            color: li.category === 'strategic' ? theme.palette.text.link : theme.palette.text.highlight,
                            '& .MuiChip-label': { p: 0 },
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            flex: 1,
                            fontSize: 11,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {li.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            color: 'text.secondary',
                            flexShrink: 0,
                            fontSize: 10,
                          }}
                        >
                          {li.points * li.quantity} pts
                        </Typography>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            className="remove-btn"
                            onClick={() => removeItem(li.lineItemId)}
                            sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25 }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )
        })}

        {/* ── Client Success Reserve ── */}
        {hasItems && (
          <Box
            sx={{
              mx: 2,
              mt: 1,
              mb: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.text.complete, 0.04),
              border: '1px solid',
              borderColor: alpha(theme.palette.text.complete, 0.12),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.text.complete, 0.15),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ fontSize: 10, color: theme.palette.text.complete }}>♡</Typography>
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
                Client Success
              </Typography>
              <Chip
                label={`${Math.round(CS_RESERVE_PCT * 100)}% reserve`}
                size="small"
                sx={{
                  height: 16,
                  fontSize: 9,
                  bgcolor: alpha(theme.palette.text.complete, 0.12),
                  color: theme.palette.text.complete,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, display: 'block', mb: 0.75 }}>
              Reserved for client communication, reporting, and success activities each month.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
                Per month
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
                {csPointsPerMonth} pts (${csMonthly.toLocaleString()})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
                {duration}-month total
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
                {csTotalPoints} pts (${csTotalDollars.toLocaleString()})
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Totals Footer ── */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
        {hasItems && (
          <>
            {/* Breakdown */}
            <Box sx={{ mb: 1.5 }}>
              <Row label="Setup" value={`${setupPoints} pts ($${setupDollars.toLocaleString()})`} />
              <Row
                label={`Months 1–${duration}`}
                value={`${monthlyItemPoints} pts ($${monthlyDollars.toLocaleString()})`}
              />
              <Row
                label="Avg. per month"
                value={`~${avgPerMonth} pts/mo (~$${Math.round(avgPerMonthDollars).toLocaleString()}/mo)`}
                muted
              />
              <Row
                label="Client Success"
                value={`${csTotalPoints} pts ($${csTotalDollars.toLocaleString()})`}
                color={theme.palette.text.complete}
              />
              <Divider sx={{ my: 0.75 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.complete }}>
                  Total engagement
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {totalEngagementPoints} pts (${totalEngagementDollars.toLocaleString()})
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Service line subtotals */}
        {serviceSubtotals.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            {serviceSubtotals.map(sl => (
              <Row key={sl.code} label={`${sl.label} (${sl.count})`} value={`${sl.points} pts`} muted />
            ))}
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none' }}
          >
            Get AI Advice
          </Button>
          <Button
            fullWidth
            variant="contained"
            size="medium"
            startIcon={<RocketLaunchIcon sx={{ fontSize: 16 }} />}
            disabled={state.lineItems.length === 0}
            onClick={() => dispatch({ type: 'SET_VIEW', view: 'review' })}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: theme.palette.warning.main,
              color: theme.palette.warning.contrastText || '#000',
              '&:hover': { bgcolor: theme.palette.warning.dark },
            }}
          >
            Generate Deal
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

/** Simple key-value row for the footer breakdown */
function Row({ label, value, muted, color }: { label: string; value: string; muted?: boolean; color?: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
      <Typography variant="caption" sx={{ fontSize: 11, color: color || (muted ? 'text.disabled' : 'text.secondary') }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', color: muted ? 'text.disabled' : 'text.primary' }}
      >
        {value}
      </Typography>
    </Box>
  )
}

export default RoadmapSidebar
